<template>
  <div class="password-gate">
    <div class="password-card">
      <div class="lock-icon">🔒</div>
      <h2 class="tour-title">{{ tourTitle || 'Protected Tour' }}</h2>
      <p class="description">This tour is password-protected. Enter the password to view.</p>
      
      <form @submit.prevent="handleSubmit" class="password-form">
        <div class="input-wrapper">
          <input
            ref="passwordInput"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="Enter password"
            class="password-input"
            :class="{ 'input-error': errorMessage }"
            :disabled="loading"
            autocomplete="off"
          />
          <button
            type="button"
            class="toggle-visibility"
            @click="showPassword = !showPassword"
            :title="showPassword ? 'Hide password' : 'Show password'"
          >
            {{ showPassword ? '👁️' : '👁️‍🗨️' }}
          </button>
        </div>
        
        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        
        <button
          type="submit"
          class="submit-btn"
          :disabled="!password || loading"
        >
          <span v-if="loading" class="spinner"></span>
          <span v-else>View Tour</span>
        </button>
      </form>

      <div v-if="coverImage" class="cover-preview">
        <img :src="coverImage" alt="Tour preview" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

const props = defineProps<{
  tourTitle?: string
  coverImage?: string
}>()

const emit = defineEmits<{
  (e: 'verify', password: string): void
}>()

const password = ref('')
const showPassword = ref(false)
const loading = ref(false)
const errorMessage = ref('')
const passwordInput = ref<HTMLInputElement>()

onMounted(() => {
  passwordInput.value?.focus()
})

function handleSubmit() {
  if (!password.value) return
  errorMessage.value = ''
  loading.value = true
  emit('verify', password.value)
}

// Exposed so parent can set error state
defineExpose({
  setError(msg: string) {
    errorMessage.value = msg
    loading.value = false
  },
  setLoading(val: boolean) {
    loading.value = val
  }
})
</script>

<style scoped>
.password-gate {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  padding: 1rem;
}

.password-card {
  background: rgba(30, 41, 59, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 1rem;
  padding: 2.5rem;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
}

.lock-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.tour-title {
  color: #f8fafc;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.description {
  color: #94a3b8;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
}

.password-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input {
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: 2.5rem;
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 0.5rem;
  color: #f8fafc;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s;
}

.password-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

.password-input.input-error {
  border-color: #ef4444;
}

.password-input::placeholder {
  color: #64748b;
}

.toggle-visibility {
  position: absolute;
  right: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  padding: 0.25rem;
  opacity: 0.6;
}

.toggle-visibility:hover {
  opacity: 1;
}

.error-text {
  color: #ef4444;
  font-size: 0.8rem;
  text-align: left;
  margin: 0;
}

.submit-btn {
  padding: 0.75rem 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
}

.submit-btn:hover:not(:disabled) {
  background: #2563eb;
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.cover-preview {
  margin-top: 1.5rem;
  border-radius: 0.5rem;
  overflow: hidden;
  opacity: 0.6;
}

.cover-preview img {
  width: 100%;
  height: auto;
  display: block;
}

@media (max-width: 480px) {
  .password-card {
    padding: 1.5rem;
  }
  
  .tour-title {
    font-size: 1.25rem;
  }
}
</style>
